import { ChangeEvent, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { TEXT_POSITION_OPTIONS } from "@constants/text-position-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { SelectOption } from "@idw-types/common";
import {
    AXNUIContent,
    AXNUIContentFormPanel,
    AXNUILayout,
    AXNUILayoutFormPanel,
    AXNUITheme,
    AXNUIThemeFormPanel
} from "@iddataweb/idw-common-ui/types";
import { ThemingFormPanelValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingFormPanelTextSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUILayout: AXNUILayout;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingFormPanelValidationErrors;
    updateAXNUIContentFormPanelFieldValue: (key: keyof AXNUIContentFormPanel, value: string) => void;
    updateAXNUILayoutFormPanelFieldValue: (key: keyof AXNUILayoutFormPanel, value: string | boolean) => void;
    updateAXNUIThemeFormPanelFieldValue: (key: keyof AXNUIThemeFormPanel, value: string) => void;
};

export const ThemingFormPanelTextSection = (props: ThemingFormPanelTextSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiContent,
        axnuiLayout,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUILayout,
        inheritedAXNUITheme,
        pageLevel,
        validationErrors,
        updateAXNUIContentFormPanelFieldValue,
        updateAXNUILayoutFormPanelFieldValue,
        updateAXNUIThemeFormPanelFieldValue
    } = props;


    const handleAddFormPanelTextChange = (newValue: string) => {
        updateAXNUILayoutFormPanelFieldValue("showFormPanelTitle", isStringTrue(newValue));
    };

    const handleResetAddFormPanelText = () => {
        updateAXNUILayoutFormPanelFieldValue("showFormPanelTitle", inheritedAXNUILayout.formPanel.showFormPanelTitle);
    };

    const handleFormPanelTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormPanelFieldValue("title", event.target.value);
    };

    const handleResetFormPanelText = () => {
        updateAXNUIContentFormPanelFieldValue("title", inheritedAXNUIContent.formPanel.title);
    };

    const handleFormPanelTitlePositionChange = (newValue: SelectOption) => {
        updateAXNUILayoutFormPanelFieldValue("alignFormPanelTitle", newValue.label);
    };

    const handleResetFormPanelTitlePosition = () => {
        updateAXNUILayoutFormPanelFieldValue("alignFormPanelTitle", inheritedAXNUILayout.formPanel.alignFormPanelTitle);
    };

    const handleFormPanelTitleTextColorChange = (newValue: string) => {
        updateAXNUIThemeFormPanelFieldValue("titleColor", newValue);
    };

    const handleResetFormPanelTitleTextColor = () => {
        updateAXNUIThemeFormPanelFieldValue("titleColor", inheritedAXNUITheme.formPanel.titleColor);
    };

    const selectedFormPanelTitlePositionOption = useMemo(() => {
        if (axnuiLayout.formPanel.alignFormPanelTitle) {
            return TEXT_POSITION_OPTIONS.find(po => po.label === axnuiLayout.formPanel.alignFormPanelTitle);
        }
        return null;
    }, [axnuiLayout.formPanel.alignFormPanelTitle]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUILayout.formPanel.showFormPanelTitle}
                currentThemingPropertyValue={axnuiLayout.formPanel.showFormPanelTitle}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`theming-form-panel-add-form-panel-title-radio-input`}
                    automationId={`theming-form-panel-add-form-panel-title-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.addFormPanelTitle.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAddFormPanelTextChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={axnuiLayout.formPanel.showFormPanelTitle ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.addFormPanelTitle.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAddFormPanelText}
                />
            </ThemingFormGroupWrapper>
            {axnuiLayout.formPanel.showFormPanelTitle && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.formPanel.title}
                        currentThemingPropertyValue={axnuiContent.formPanel.title}
                        pageLevel={pageLevel}
                    >
                        <InputFormGroup
                            controlId={"theming-form-panel-title-input"}
                            automationId={"theming-form-panel-title-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.formPanelTitle.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.formPanelTitle.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleFormPanelTextChange}
                            value={axnuiContent.formPanel.title || ""}
                            disabled={isDisabled}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.formPanelTitle.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetFormPanelText}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.formPanelTitleError}
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUILayout.formPanel.alignFormPanelTitle}
                        currentThemingPropertyValue={axnuiLayout.formPanel.alignFormPanelTitle}
                        pageLevel={pageLevel}
                    >
                        <SelectFormGroup
                            controlId={"theming-form-panel-title-postition-select-input"}
                            automationId={"theming-form-panel-title-position-select-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.formPanelTitlePosition.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.formPanelTitlePosition.label}
                            disabled={isDisabled}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={false}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleFormPanelTitlePositionChange}
                            options={TEXT_POSITION_OPTIONS}
                            selectedOption={selectedFormPanelTitlePositionOption}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.formPanelTitlePosition.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetFormPanelTitlePosition}
                        />
                    </ThemingFormGroupWrapper>

                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUITheme.formPanel.titleColor}
                        currentThemingPropertyValue={axnuiTheme.formPanel.titleColor}
                        pageLevel={pageLevel}
                    >
                        <ColorPickerFormGroup
                            controlId={"theming-form-panel-title-text-color-picker"}
                            automationId={"theming-form-panel-title-text-color-picker"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.formPanelTitleTextColor.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.formPanelTitleTextColor.label}
                            disabled={isDisabled}
                            isRequired={true}
                            layout={layout}
                            labelWidth={labelWidth}
                            value={axnuiTheme.formPanel.titleColor || ""}
                            onChange={handleFormPanelTitleTextColorChange}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formPanel.sections.formPanelTitle.inputs.formPanelTitleTextColor.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetFormPanelTitleTextColor}
                        />
                    </ThemingFormGroupWrapper>
                </>
            )}
        </div>
    );
};